import React, { useEffect, useMemo, useState } from 'react';
import RepWorkingTimeReports from './RepWorkingTimeReports.component';
import { i18nService } from '../../../../services/i18n.service';
import { useDispatch, useSelector } from 'react-redux';
import { getClientsData, getCompaniesData, getExcelClientsData, getExcelCompaniesData, getPieClientsData, getPieCompaniesData } from '../../../../store/selectors/workingTime.selector';
import { getOperatorIdList, getOperators } from '../../../../store/selectors/operators.selector';
import { fetchOperatorsList } from '../../../../store/actions/operators.actions';
import { fetchClientsAndCompanysData } from '../../../../store/actions/workingTime.action';
import httpService from '../../../../services/http.service';
import { getSelectedUnits } from '../../rep.utils';
import { exportToExcel } from '../../../../services/excel.service';
import Snackbar from '@material-ui/core/Snackbar';

interface companyWorkingTimeDataType {
  id: string;
  companyName: string;
  companyId?: string;
  hours: string;
  payslipsUploaded: number;
  forms101Processed: number;
  forms106Uploaded: number;
  color?: string;
};

interface accountantWorkingTimeDataType {
  id: string;
  employeeName: string;
  employeeId?: string;
  hours: string;
  companysHandled: number;
  color?: string;
}

const RepWorkingTimeReportsContainer = (props: any) => {
  const { navigation } = props;
  const dispatch = useDispatch();
  const companyTableData = useSelector(getCompaniesData);
  const companyPieData = useSelector(getPieCompaniesData);
  const accountantTableData = useSelector(getClientsData); 
  const accountantPieData = useSelector(getPieClientsData);
  const accountantsIds = useSelector(getOperatorIdList);
  const companiesExcelData = useSelector(getExcelCompaniesData);
  const clientsExcelData = useSelector(getExcelClientsData);
  // const operators = useSelector(getOperators);
  const [businessUnitsList, setBusinessUnitsList] = useState<any[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
 
  // Only fetch if we don't have data
  const fetchBusinessUnits = async () => {
	try {
	  const res: any = await httpService.api<string[]>({
		type: 'getRepresntUnits',
		query: { pageName: 'repCustomers' },
	  });

	  const mappedUnits = res?.map((item: any) => ({
		...item,
		id: item.numericId,
		stringId: item.id,
	  })) || [];
	  
	  setBusinessUnitsList(mappedUnits);
	  
	  // Fetch operators with the first unit as default
	  if (mappedUnits.length > 0) {
		const unitIds = getSelectedUnits([mappedUnits[0]?.id], mappedUnits);
		dispatch(fetchOperatorsList(unitIds));
	  }
	  setIsInitialized(true);
	} catch (error) {
	  console.error('Error fetching business units:', error);
	  setIsInitialized(true);
	}
  };

  // Fetch business units on mount
  useEffect(() => {   
	fetchBusinessUnits();
  }, []);

  // Fetch working time data when operators are available
  useEffect(() => {
    if (isInitialized && accountantsIds.length > 0 && (companyTableData.length === 0 || accountantTableData.length === 0)) {
      dispatch(fetchClientsAndCompanysData(accountantsIds));
    }
  }, [isInitialized, accountantsIds, businessUnitsList, dispatch]);


  	const CustomHeaderContent = ({ mainText, subText }: { mainText: any, subText: any }) => (
		<div
			style={{
				display: 'flex',
				flexDirection: 'column',
				alignItems: 'center',
			}}
		>
			<div style={{ fontWeight: 'bold' }}>{mainText}</div>
			<div style={{ fontWeight: 'normal', fontSize: '0.85em' }}>
				{subText}
			</div>
		</div>
	);

  	const companyTableHeaders = [
		{
			Header: (
				<CustomHeaderContent mainText={i18nService.translate('repReports.tableHeader.mainTitle.companyName')} subText={i18nService.translate('repReports.tableHeader.subTitle.inSoftware')} />
			),
			accessor: 'companyName',
			isSortable: true,
			CustomCell: ({ cell, row }: { cell: any, row: any }) => (
				(
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: 10,
						}}
					>
						<div
							style={{
								background: row.original.color,
								height: '8px',
								width: '8px',
								borderRadius: '20px',
							}}
						></div>
						<span
						>
							{cell.value}
						</span>
					</div>
				)
			),
      
		},
		{
			Header: (
				<CustomHeaderContent
					mainText={i18nService.translate('repReports.tableHeader.mainTitle.workingTime')}
					subText={i18nService.translate('repReports.tableHeader.subTitle.inSoftware')}
				/>
			),
			accessor: 'hours',
			isSortable: true,
      CustomCell: ({ cell: { value } }: { cell: { value: string } }) => (
        <span >{value} ש'</span>
      ),
		},
		{
			Header: (
				<CustomHeaderContent
					mainText={i18nService.translate('repReports.tableHeader.mainTitle.payslips')}
					subText={i18nService.translate('repReports.tableHeader.subTitle.uploadedToPortal')}
				/>
			),
			accessor: 'payslipsUploaded',
			isSortable: true,
		},
		{
			Header: (
				<CustomHeaderContent
					mainText={i18nService.translate('repReports.tableHeader.mainTitle.forms101')}
					subText={i18nService.translate('repReports.tableHeader.subTitle.caredInPortal')}
				/>
			),
			accessor: 'forms101Processed',
			isSortable: true,
		},
	];

    const accountantTableHeaders = [
		{
			Header: (
				<CustomHeaderContent mainText={i18nService.translate('repReports.tableHeader.mainTitle.accountantName')} subText={i18nService.translate('repReports.tableHeader.subTitle.inSoftware')} />
			),
			accessor: 'employeeName',
			isSortable: true,
			CustomCell: ({ cell, row }: { cell: any, row: any }) => (
				(
					<div
						style={{
							display: 'flex',
							flexDirection: 'row',
							alignItems: 'center',
							gap: 10,
						}}
					>
						<div
							style={{
								background: row.original.color,
								height: '8px',
								width: '8px',
								borderRadius: '20px',
							}}
						></div>
						<span>
							{cell.value}
						</span>
					</div>
				)
			),
		},
		{
			Header: (
				<CustomHeaderContent
					mainText={i18nService.translate('repReports.tableHeader.mainTitle.totalWorkingTime')}
					subText={i18nService.translate('repReports.tableHeader.subTitle.inSoftware')}
				/>
			),
			accessor: 'hours',
      CustomCell: ({ cell: { value } }: { cell: { value: string } }) => (
        <span >{value} ש'</span>
      ),
			isSortable: true,
		},
		{
			Header: (
				<CustomHeaderContent
					mainText={i18nService.translate('repReports.tableHeader.mainTitle.handledCompanys')}
					subText={i18nService.translate('repReports.tableHeader.subTitle.inSoftware')}
				/>
			),
			accessor: 'companysHandled',
			isSortable: true,
		},
	];

  
  //  Show loading state while fetching data
  if (!isInitialized || companyTableData.length === 0 || accountantTableData.length === 0) {
    return (
      <RepWorkingTimeReports
        companyTableData={[]}
        companyTableHeaders={companyTableHeaders}
        companyPieData={[]}
        accountantTableData={[]}
        accountantTableHeaders={accountantTableHeaders}
        accountantPieData={[]}
        navigation={navigation}
      />
    );
  }

  const handleCompaniesExcelExport = async () => {
	const dateStr = new Date().toLocaleString('en-IL', {hour12: false})
                   .replace(/, /g, '_') // Replace comma+space with underscore
                   .replaceAll('/', '-')
                   .replaceAll(':', '-')
                   .replaceAll(' ', '_');
	try {
		await exportToExcel( {
			fileName: `${i18nService.translate('repReports.workingTimeReports.timeAndDelivery.Company')}${" " + dateStr}.xlsx`,
			sheetName: 'Companies Working Time',
			title: i18nService.translate('repReports.workingTimeReports.timeAndDelivery.Company'),
			data: companiesExcelData,
			columns: [
				{
					header: i18nService.translate('repReports.companiesExcel.companyName'),
					key: 'companyName',
					width: 30,
				},
				{
					header: i18nService.translate('repReports.companiesExcel.workingTime'),
					key: 'totalHours',
					width: 30,
				},
				{
					header: i18nService.translate('repReports.companiesExcel.payslips'),
					key: 'payslipsUploaded',
					width: 30,
				},
				{
					header: i18nService.translate('repReports.companiesExcel.forms101'),
					key: 'forms101Processed',
					width: 30,
				},
			],
		});
	} catch (error) {
		setSnackbarOpen(true);
		setSnackbarMessage(i18nService.translate('excelToastError'));
	}
  }

const handleClientsExcelExport = async () => {
	const dateStr = new Date().toLocaleString('en-IL', {hour12: false})
				.replace(/, /g, '_') // Replace comma+space with underscore
				.replaceAll('/', '-')
				.replaceAll(':', '-')
				.replaceAll(' ', '_');
	try {
		await exportToExcel( {
			fileName: `${i18nService.translate('repReports.workingTimeReports.timeAndDelivery.Accountant')}${" " + dateStr}.xlsx`,
			sheetName: 'Clients Working Time',
			title: i18nService.translate('repReports.workingTimeReports.timeAndDelivery.Accountant'),
			data: clientsExcelData,
			columns: [
				{
					header: i18nService.translate('repReports.clientsExcel.employeeName'),
					key: 'employeeName',
					width: 30,
				},
				{
					header: i18nService.translate('repReports.clientsExcel.workingTime'),
					key: 'totalHours',
					width: 30,
				},
				{
					header: i18nService.translate('repReports.clientsExcel.handledCompanys'),
					key: 'handledCompanys',
					width: 30,
				},
			],
		});
	} catch (error) {
		setSnackbarOpen(true);
		setSnackbarMessage(i18nService.translate('excelToastError'));
	}
}

  return (
    <>
      <RepWorkingTimeReports
        companyTableData={companyTableData}
        companyTableHeaders={companyTableHeaders}
        companyPieData={companyPieData}
		companyExcelExportHandler={handleCompaniesExcelExport}
        accountantTableData={accountantTableData}
        accountantTableHeaders={accountantTableHeaders}
        accountantPieData={accountantPieData}
		accountantExcelExportHandler={handleClientsExcelExport}
        navigation={navigation}
      />

	<Snackbar 
			anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
			open={snackbarOpen}
			autoHideDuration={4000}
			onClose={() => setSnackbarOpen(false)}
			ContentProps={{
				style: {
				direction: 'rtl',
					backgroundColor: '#d32f2f',
					color: '#fff',
					fontWeight: 'bold',
					fontSize: '14px',
					borderRadius: '4px',
					boxShadow: '0px 3px 5px -1px rgba(0,0,0,0.2), 0px 6px 10px 0px rgba(0,0,0,0.14)',
				}
			}}
			message={
				<span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
					{snackbarMessage}
				</span>
			}
		/>
    </>
  );
};

export default RepWorkingTimeReportsContainer;